import React, { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { CompareProvider } from './context/CompareContext';
import useUserLocation from './hooks/useUserLocation';
import usePageTracking from './hooks/usePageTracking';
import { endUserTiming, startUserTiming } from './utils/userTiming';
import useFetchTreks from './hooks/useFetchTreks';
import { ErrorBoundary, MobileBottomNav, PageTransition, SkeletonDetail, ToastContainer } from './design-system';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedEditRoute from './components/ProtectedEditRoute';
import AdminRoute from './components/AdminRoute';
import PartnerRoute from './components/PartnerRoute';
import CompareBar from './components/CompareBar';
import apiClient from './utils/apiClient';
import { useGlobalShortcut } from './hooks/useGlobalShortcut';
import ConsentBanner from './components/ConsentBanner';
import { preloadHomeRoutes } from './utils/routePreloader';

const lazyNamed = (loader, exportName) => lazy(() => loader().then((module) => ({ default: module[exportName] })));

const loadInfoPages = () => import('./pages/InfoPages');

const Home = lazy(() => import('./pages/Home'));
const Treks = lazy(() => import('./pages/Treks'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const TrekDetails = lazy(() => import('./pages/TrekDetails'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const About = lazyNamed(loadInfoPages, 'About');
const PrivacyPolicy = lazyNamed(loadInfoPages, 'PrivacyPolicy');
const TermsOfUse = lazyNamed(loadInfoPages, 'TermsOfUse');
const CookiePolicy = lazyNamed(loadInfoPages, 'CookiePolicy');
const Community = lazyNamed(loadInfoPages, 'Community');
const FAQs = lazyNamed(loadInfoPages, 'FAQs');
const CancellationRefundPolicy = lazyNamed(loadInfoPages, 'CancellationRefundPolicy');
const Disclaimer = lazyNamed(loadInfoPages, 'Disclaimer');
const HelpCenter = lazyNamed(loadInfoPages, 'HelpCenter');
const Regions = lazy(() => import('./pages/Regions'));
const Contact = lazy(() => import('./pages/Contact'));
const SavedTreks = lazy(() => import('./pages/SavedTreks'));
const OperatorTreksAdmin = lazy(() => import('./pages/OperatorTreksAdmin'));
const PartnerOffersView = lazy(() => import('./pages/PartnerOffersView'));
const PartnerOfferDetail = lazy(() => import('./pages/PartnerOfferDetail'));
const BestTreksNearCityForBeginners = lazy(() => import('./pages/BestTreksNearCityForBeginners'));
const AllIndiaEasyTreks = lazy(() => import('./pages/AllIndiaEasyTreks'));
const AllIndiaModerateTreks = lazy(() => import('./pages/AllIndiaModerateTreks'));
const AllIndiaDifficultTreks = lazy(() => import('./pages/AllIndiaDifficultTreks'));
const BestEasyHighAltitudeTreks = lazy(() => import('./pages/BestEasyHighAltitudeTreks'));
const TopHighAltitudeTreks = lazy(() => import('./pages/TopHighAltitudeTreks'));
const TopLongDistanceTreks = lazy(() => import('./pages/TopLongDistanceTreks'));
const BestEasyLongDistanceTreks = lazy(() => import('./pages/BestEasyLongDistanceTreks'));
const BestEasyWeekendTreks = lazy(() => import('./pages/BestWeekendTreks'));
const BestTreksInRegion = lazy(() => import('./pages/BestTreksInRegion'));
const SnowTreks = lazy(() => import('./pages/SnowTreks'));
const SeasonTreks = lazy(() => import('./pages/SeasonTreks'));
const MonthTreks = lazy(() => import('./pages/MonthTreks'));
const BestTrekPackages = lazy(() => import('./pages/BestTrekPackages'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const InfluencerProgram = lazy(() => import('./pages/InfluencerProgram'));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs'));
const ReportIssue = lazy(() => import('./pages/ReportIssue'));
const NotFound = lazy(() => import('./pages/NotFound'));

const RouteBoundary = ({ name, children }) => (
    <ErrorBoundary name={name}>
        <PageTransition>
            {children}
        </PageTransition>
    </ErrorBoundary>
);

RouteBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
};

function AppContent({ token, authLoading, setToken, logout }) {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (pathname === '/maintenance') return;
        apiClient.get('/maintenance/status').then(res => {
            if (res.data?.data?.enabled && !pathname.startsWith('/admin') && !pathname.startsWith('/api/')) {
                navigate('/maintenance', { replace: true });
            }
        }).catch(() => {
        });
    }, [pathname, navigate]);

    const needsTrekCatalog = pathname === '/'
        || pathname === '/treks'
        || pathname === '/regions'
        || pathname === '/saved'
        || pathname === '/partner'
        || pathname.startsWith('/partners/')
        || pathname.startsWith('/partner-offers/')
        || pathname.startsWith('/nearby-treks/')
        || pathname.startsWith('/compare')
        || pathname === '/influencer'
        || pathname === '/india-easy-treks'
        || pathname === '/india-moderate-treks'
        || pathname === '/india-difficult-treks'
        || pathname === '/best-easy-high-altitude-treks'
        || pathname === '/best-high-altitude-treks'
        || pathname === '/best-long-distance-treks'
        || pathname === '/best-easy-long-distance-treks'
        || pathname === '/best-easy-weekend-treks'
        || pathname.startsWith('/easy-treks/')
        || pathname === '/snow-treks'
        || pathname.startsWith('/season/')
        || pathname.startsWith('/month/')
        || pathname.startsWith('/admin');
    const { treks: allTreks, loading: treksLoading } = useFetchTreks({ enabled: needsTrekCatalog });
    const pageProps = useMemo(
        () => ({ token, logout, allTreks, treksLoading }),
        [allTreks, logout, token, treksLoading],
    );
    const authPageProps = useMemo(() => ({ ...pageProps, setToken }), [pageProps, setToken]);

    const withBoundary = useCallback((name, element) => (
        <RouteBoundary name={name}>{element}</RouteBoundary>
    ), []);

    return (<>
        <a href="#main-content" className="skip-to-content">
            Skip to main content
        </a>
        <ToastContainer />
        <ConsentBanner />
        <main id="main-content">
            <Suspense fallback={<SkeletonDetail />}>
                <Routes>
                    <Route path="/" element={withBoundary('home-route', <Home {...pageProps} />)} />
                    <Route path="/treks" element={withBoundary('treks-route', <Treks {...pageProps} />)} />
                    <Route
                        path="/edit-profile"
                        element={withBoundary('edit-profile-route', <ProtectedEditRoute token={token}
                                                                                        authLoading={authLoading}>
                            <EditProfile {...pageProps} />
                        </ProtectedEditRoute>)}
                    />
                    <Route path="/treks/:slug"
                           element={withBoundary('trek-details-route', <TrekDetails {...authPageProps} />)} />
                    <Route path="/treks/:slug/packages"
                           element={withBoundary('best-trek-packages-route', <BestTrekPackages {...pageProps} />)} />
                    <Route path="/login"
                           element={withBoundary('login-route', <Login {...authPageProps} />)} />
                    <Route path="/signup"
                           element={withBoundary('signup-route', <Signup {...authPageProps} />)} />
                    <Route path="/partner/login"
                           element={withBoundary('partner-login-route', <Login {...authPageProps} />)} />
                    <Route path="/partner/signup"
                           element={withBoundary('partner-signup-route', <Signup {...authPageProps} />)} />
                    <Route path="/profile"
                           element={withBoundary('user-profile-route', <UserProfile {...authPageProps} />)} />
                    <Route path="/users/:userId"
                           element={withBoundary('user-profile-route', <UserProfile {...authPageProps} />)} />
                    <Route path="/forgot-password"
                           element={withBoundary('forgot-password-route', <ForgotPassword {...pageProps} />)} />
                    <Route path="/reset-password"
                           element={withBoundary('reset-password-route', <ResetPassword {...pageProps} />)} />
                    <Route path="/about" element={withBoundary('about-route', <About {...pageProps} />)} />
                    <Route path="/privacy-policy"
                           element={withBoundary('privacy-policy-route', <PrivacyPolicy {...pageProps} />)} />
                    <Route path="/terms-of-use"
                           element={withBoundary('terms-route', <TermsOfUse {...pageProps} />)} />
                    <Route path="/cookie-policy"
                           element={withBoundary('cookie-policy-route', <CookiePolicy {...pageProps} />)} />
                    <Route path="/community"
                           element={withBoundary('community-route', <Community {...pageProps} />)} />
                    <Route path="/unsubscribe/:token"
                           element={withBoundary('unsubscribe-route', <Unsubscribe {...pageProps} />)} />
                    <Route path="/verify-email/:token"
                           element={withBoundary('verify-email-route', <VerifyEmail {...pageProps} />)} />
                    <Route path="/faqs" element={withBoundary('faqs-route', <FAQs {...pageProps} />)} />
                    <Route path="/regions" element={withBoundary('regions-route', <Regions {...pageProps} />)} />
                    <Route path="/contact"
                           element={withBoundary('contact-route', <Contact {...pageProps} />)} />
                    <Route path="/saved" element={withBoundary('saved-route', <SavedTreks {...pageProps} />)} />
                    <Route path="/partner"
                           element={withBoundary('operator-admin-route', <PartnerRoute
                               token={token}
                               authLoading={authLoading}><OperatorTreksAdmin {...pageProps} /></PartnerRoute>)} />
                    <Route path="/nearby-treks/:city"
                           element={withBoundary('nearby-treks-route',
                               <BestTreksNearCityForBeginners {...pageProps} />)} />
                    <Route path="/india-easy-treks"
                           element={withBoundary('india-easy-treks-route',
                               <AllIndiaEasyTreks {...pageProps} />)} />
                    <Route path="/india-moderate-treks"
                           element={withBoundary('india-moderate-treks-route',
                               <AllIndiaModerateTreks {...pageProps} />)} />
                    <Route path="/india-difficult-treks"
                           element={withBoundary('india-difficult-treks-route',
                               <AllIndiaDifficultTreks {...pageProps} />)} />
                    <Route path="/best-easy-high-altitude-treks"
                           element={withBoundary('best-easy-high-altitude-treks-route',
                               <BestEasyHighAltitudeTreks {...pageProps} />)} />
                    <Route path="/best-high-altitude-treks"
                           element={withBoundary('best-high-altitude-treks-route',
                               <TopHighAltitudeTreks {...pageProps} />)} />
                    <Route path="/best-long-distance-treks"
                           element={withBoundary('best-long-distance-treks-route',
                               <TopLongDistanceTreks {...pageProps} />)} />
                    <Route path="/best-easy-long-distance-treks"
                           element={withBoundary('best-easy-long-distance-treks-route',
                               <BestEasyLongDistanceTreks {...pageProps} />)} />
                    <Route path="/best-easy-weekend-treks"
                           element={withBoundary('best-easy-weekend-treks-route',
                               <BestEasyWeekendTreks {...pageProps} />)} />
                    <Route path="/easy-treks/:regionSlug"
                           element={withBoundary('region-easy-treks-route',
                               <BestTreksInRegion {...pageProps} />)} />
                    <Route path="/snow-treks"
                           element={withBoundary('snow-treks-route',
                               <SnowTreks {...pageProps} />)} />
                    <Route path="/season/:seasonName"
                           element={withBoundary('season-treks-route',
                               <SeasonTreks {...pageProps} />)} />
                    <Route path="/month/:monthName"
                           element={withBoundary('month-treks-route',
                               <MonthTreks {...pageProps} />)} />
                    <Route path="/partners/:partnerSlug"
                           element={withBoundary('partner-offers-route', <PartnerOffersView token={token}
                                                                                            logout={logout}
                                                                                            allTreks={allTreks} />)}
                    />
                    <Route path="/partner-offers/:slug"
                           element={withBoundary('partner-offer-detail-route', <PartnerOfferDetail token={token}
                                                                                                   logout={logout}
                                                                                                   allTreks={allTreks} />)}
                    />
                    <Route path="/compare"
                           element={withBoundary('compare-route', <ComparePage {...pageProps} />)} />

                    <Route path="/influencer"
                           element={withBoundary('influencer-route', <InfluencerProgram {...pageProps} />)} />
                    <Route path="/partner-with-us"
                           element={withBoundary('partner-with-us-route', <PartnerWithUs {...pageProps} />)} />
                    <Route path="/report-issue"
                           element={withBoundary('report-issue-route', <ReportIssue {...pageProps} />)} />
                    <Route path="/cancellation-refund-policy"
                           element={withBoundary('cancellation-refund-policy-route',
                               <CancellationRefundPolicy {...pageProps} />)} />
                    <Route path="/disclaimer"
                           element={withBoundary('disclaimer-route', <Disclaimer {...pageProps} />)} />
                    <Route path="/help-center"
                           element={withBoundary('help-center-route', <HelpCenter {...pageProps} />)} />
                    <Route path="/maintenance"
                           element={withBoundary('maintenance-route', <MaintenancePage />)} />
                    <Route path="/admin"
                           element={withBoundary('admin-dashboard-route', <AdminRoute
                               token={token}
                               authLoading={authLoading}><AdminDashboard {...pageProps} /></AdminRoute>)} />
                    <Route path="*"
                           element={withBoundary('not-found-route', <NotFound {...pageProps} />)} />
                </Routes>
            </Suspense>
        </main>
        <CompareBar />
        <Footer />
        <MobileBottomNav token={token} />
    </>);
}

AppContent.propTypes = {
    logout: PropTypes.func.isRequired,
    setToken: PropTypes.func.isRequired,
    token: PropTypes.string,
    authLoading: PropTypes.bool,
};

AppContent.defaultProps = {
    token: '',
    authLoading: true,
};

function AppWithTracking({ token, authLoading, setToken, logout }) {
    usePageTracking();
    return <AppContent token={token} authLoading={authLoading} setToken={setToken} logout={logout} />;
}

AppWithTracking.propTypes = AppContent.propTypes;

AppWithTracking.defaultProps = AppContent.defaultProps;

function AppInner() {
    const { token, authLoading, setToken, logout } = useAuthContext();
    const navigate = useNavigate();

    useEffect(() => {
        startUserTiming('app_bootstrap_start');
        preloadHomeRoutes();
    }, []);

    const { ready: locationReady } = useUserLocation();

    useEffect(() => {
        if (!locationReady) return;
        startUserTiming('app_bootstrap_end');
        endUserTiming({
            measureName: 'app_bootstrap_duration', startMark: 'app_bootstrap_start', endMark: 'app_bootstrap_end',
        });
    }, [locationReady]);

    useGlobalShortcut();

    useEffect(() => {
        const onAuthExpired = (e) => {
            const returnPath = e.detail?.returnPath || '/';
            const isPartnerPortal = returnPath.startsWith('/partner')
                && !returnPath.startsWith('/partners')
                && !returnPath.startsWith('/partner-offers');
            const loginPath = isPartnerPortal ? '/partner/login' : '/login';
            navigate(loginPath, { state: { from: { pathname: returnPath } }, replace: true });
        };
        window.addEventListener('ftr:auth-expired', onAuthExpired);
        return () => window.removeEventListener('ftr:auth-expired', onAuthExpired);
    }, [navigate]);

    useEffect(() => {
        const onConsent = async (e) => {
            const detail = e.detail;
            if (token) {
                try {
                    await apiClient.post('/consent', detail, { headers: { Authorization: `Bearer ${token}` } });
                } catch (err) {
                    // ignore
                }
            }
        };
        window.addEventListener('ftr_consent_changed', onConsent);
        return () => window.removeEventListener('ftr_consent_changed', onConsent);
    }, [token]);

    return (
        <AppWithTracking token={token} authLoading={authLoading} setToken={setToken} logout={logout} />
    );
}

function App() {
    return (<AuthProvider>
        <Router>
            <ScrollToTop />
            <CompareProvider>
                <AppInner />
            </CompareProvider>
        </Router>
    </AuthProvider>);
}

export default App;
